import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { 
  Bluetooth, 
  Search, 
  Battery, 
  RefreshCw, 
  CheckCircle2, 
  XCircle,
  Loader2,
  AlertTriangle,
  Info
} from 'lucide-react';
import { 
  scanForDevices, 
  connectToDevice, 
  disconnectDevice,
  getDeviceBatteryLevel
} from '@/lib/bluetooth';

interface BluetoothManagerProps {
  onDeviceConnected?: (deviceId: string) => void;
  onDeviceDisconnected?: (deviceId: string) => void;
}

export function BluetoothManager({ onDeviceConnected, onDeviceDisconnected }: BluetoothManagerProps) {
  const [scanning, setScanning] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [bluetoothAvailable, setBluetoothAvailable] = useState(true);
  const [devices, setDevices] = useState<Array<{
    id: string;
    name: string;
    connected: boolean;
    batteryLevel?: number;
  }>>([]);
  const { toast } = useToast();

  // Vérifier la disponibilité du Bluetooth au chargement
  useEffect(() => {
    const checkBluetoothAvailability = () => {
      const isAvailable = 'bluetooth' in navigator;
      setBluetoothAvailable(isAvailable);
      
      if (!isAvailable) {
        toast({
          variant: "destructive",
          title: "Bluetooth non disponible",
          description: "Votre navigateur ne supporte pas le Bluetooth. Veuillez utiliser un navigateur compatible comme Chrome.",
        });
      }
    };

    checkBluetoothAvailability();
  }, [toast]);

  const handleScan = async () => {
    try {
      if (!bluetoothAvailable) {
        toast({
          variant: "destructive",
          title: "Bluetooth non disponible",
          description: "Votre navigateur ne supporte pas le Bluetooth.",
        });
        return;
      }

      setScanning(true);
      const foundDevices = await scanForDevices();
      setDevices(foundDevices);

      // Si aucun appareil n'est trouvé après un scan réussi
      if (foundDevices.length === 0) {
        toast({
          title: "Aucun appareil trouvé",
          description: "Assurez-vous que votre Samsung Galaxy Fit 3 est en mode appairage.",
          icon: <Info className="w-4 h-4" />,
        });
      }
    } catch (error: any) {
      // Ne pas afficher d'erreur si l'utilisateur a simplement annulé
      if (error.name === 'NotFoundError' && error.message.includes('cancelled')) {
        return;
      }

      // Gérer les autres erreurs
      let errorMessage = "Impossible de scanner les appareils Bluetooth.";
      
      if (error.name === 'SecurityError') {
        errorMessage = "Veuillez autoriser l'accès au Bluetooth dans les paramètres de votre navigateur.";
      } else if (error.name === 'NotAllowedError') {
        errorMessage = "L'accès au Bluetooth a été refusé.";
      }

      toast({
        variant: "destructive",
        title: "Erreur de scan",
        description: errorMessage,
      });
    } finally {
      setScanning(false);
    }
  };

  const handleConnect = async (deviceId: string) => {
    try {
      setConnecting(true);
      await connectToDevice(deviceId);
      
      // Mettre à jour l'état local
      setDevices(prev => prev.map(device => 
        device.id === deviceId 
          ? { ...device, connected: true }
          : device
      ));

      try {
        // Récupérer le niveau de batterie
        const batteryLevel = await getDeviceBatteryLevel(deviceId);
        setDevices(prev => prev.map(device => 
          device.id === deviceId 
            ? { ...device, batteryLevel }
            : device
        ));
      } catch (batteryError) {
        // Ne pas bloquer la connexion si la lecture de la batterie échoue
        console.warn('Impossible de lire le niveau de batterie:', batteryError);
      }

      onDeviceConnected?.(deviceId);

      toast({
        title: "Appareil connecté",
        description: "La connexion Bluetooth a été établie avec succès.",
      });
    } catch (error: any) {
      let errorMessage = "Impossible de se connecter à l'appareil.";
      
      if (error.name === 'NetworkError') {
        errorMessage = "L'appareil est hors de portée ou éteint.";
      } else if (error.name === 'NotAllowedError') {
        errorMessage = "La connexion a été refusée par l'appareil.";
      }

      toast({
        variant: "destructive",
        title: "Erreur de connexion",
        description: errorMessage,
      });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async (deviceId: string) => {
    try {
      await disconnectDevice(deviceId);
      
      setDevices(prev => prev.map(device => 
        device.id === deviceId 
          ? { ...device, connected: false }
          : device
      ));

      onDeviceDisconnected?.(deviceId);

      toast({
        title: "Appareil déconnecté",
        description: "La déconnexion Bluetooth a été effectuée avec succès.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur de déconnexion",
        description: "Impossible de déconnecter l'appareil. Veuillez réessayer.",
      });
    }
  };

  if (!bluetoothAvailable) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-3 text-orange-600">
            <AlertTriangle className="w-5 h-5" />
            <div>
              <h3 className="font-medium">Bluetooth non disponible</h3>
              <p className="text-sm text-gray-600 mt-1">
                Votre navigateur ne supporte pas le Bluetooth. Veuillez utiliser un navigateur compatible comme Chrome.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bluetooth className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold">Gestion Bluetooth</h2>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <Button
            onClick={handleScan}
            disabled={scanning}
            className="w-full gap-2"
          >
            {scanning ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Recherche en cours...
              </>
            ) : (
              <>
                <Search className="w-4 h-4" />
                Rechercher des appareils
              </>
            )}
          </Button>

          <div className="space-y-4">
            {devices.map(device => (
              <div
                key={device.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="space-y-1">
                  <div className="font-medium">{device.name}</div>
                  <div className="text-sm text-gray-500 flex items-center gap-2">
                    {device.batteryLevel !== undefined && (
                      <>
                        <Battery className="w-4 h-4" />
                        {device.batteryLevel}%
                      </>
                    )}
                  </div>
                </div>

                <Button
                  variant={device.connected ? "outline" : "default"}
                  onClick={() => device.connected 
                    ? handleDisconnect(device.id)
                    : handleConnect(device.id)
                  }
                  disabled={connecting}
                  className="gap-2"
                >
                  {connecting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : device.connected ? (
                    <XCircle className="w-4 h-4" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4" />
                  )}
                  {device.connected ? 'Déconnecter' : 'Connecter'}
                </Button>
              </div>
            ))}

            {devices.length === 0 && !scanning && (
              <div className="text-center py-4 text-gray-500">
                <p>Aucun appareil trouvé</p>
                <p className="text-sm mt-1">
                  Assurez-vous que votre Samsung Galaxy Fit 3 est en mode appairage
                </p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}